import { useTrackStorefrontInteraction } from '@hooks/useTrackStorefrontInteraction';
import { useAppSelector } from '@redux/hooks';
import { IconAction } from '@tkww/orion-web-ds-action';
import { compose } from '@xo-union/react-css-modules';
import {
	CarouselWithStaticVisibleSlots,
	type NavigationButtonProps,
} from '@xo-union/ui-carousel';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import React, { type MouseEventHandler, useCallback } from 'react';
import type { CategoryCode } from 'types/Category';
import {
	type DetailedPricingPackage,
	packagesAtom,
} from '../../jotai/storefront/detailed-pricing';
import { Package } from './Package';
import { PackageVenue } from './PackageVenue';
import Styles from './packages.scss';

const PrevButton = ({
	className,
	onClick,
	...props
}: NavigationButtonProps) => {
	const track = useTrackStorefrontInteraction('Packages');

	const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
		(e) => {
			track('Click left');

			if (onClick) {
				onClick(e);
			}
		},
		[track, onClick],
	);

	return (
		<div className={Styles.iconWrapper} aria-roledescription="previous button">
			<IconAction
				className={classNames(Styles.previousButton, className)}
				name="caret-left"
				size="md"
				onClick={handleClick}
				{...props}
			/>
		</div>
	);
};

const NextButton = ({
	className,
	onClick,
	...props
}: NavigationButtonProps) => {
	const track = useTrackStorefrontInteraction('Packages');

	const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
		(e) => {
			track('Click right');

			if (onClick) {
				onClick(e);
			}
		},
		[track, onClick],
	);

	return (
		<div className={Styles.iconWrapper} aria-roledescription="next button">
			<IconAction
				className={classNames(Styles.nextButton, className)}
				name="caret-right"
				size="md"
				onClick={handleClick}
				{...props}
			/>
		</div>
	);
};

const PackagePicker = ({
	categoryCode,
	pkg,
}: { categoryCode: CategoryCode; pkg: DetailedPricingPackage }) => {
	if (categoryCode === 'REC') {
		return <PackageVenue pkg={pkg} />;
	}

	return <Package initialCollapse={false} pkg={pkg} />;
};

export const Packages = () => {
	const packages = useAtomValue(packagesAtom);
	const categoryCode = useAppSelector(
		(state) => state.vendor.vendor?.categoryCode as CategoryCode,
	);

	if (!packages.length) {
		return null;
	}

	return (
		<>
			<div className={classNames(Styles.hideOnDesktop, Styles.list)}>
				{packages.map((pkg, index) => {
					const key = `pkg-${index}-mobile`;
					return (
						<PackagePicker pkg={pkg} categoryCode={categoryCode} key={key} />
					);
				})}
			</div>
			<div className={classNames(Styles.hideOnMobile, Styles.carousel)}>
				<CarouselWithStaticVisibleSlots
					classes={compose({ container: Styles.carouselContainer })}
					visibleSlots={3}
					renderNextButton={NextButton}
					renderPrevButton={PrevButton}
					disableDrag
				>
					{packages.map((pkg, index) => {
						const key = `pkg-${index}-desktop`;
						return (
							<PackagePicker pkg={pkg} categoryCode={categoryCode} key={key} />
						);
					})}
				</CarouselWithStaticVisibleSlots>
			</div>
		</>
	);
};
